import {
  AppEvent,
  AppEventId,
  DEFAULT_HOST_SETUP_OPTIONS,
  InitResult,
} from "@loader-event-bus/app-event.model";

let resolvedInitEvent: InitResult | null = null;
let pendingResolvers: Array<(result: InitResult) => void> = [];

/**
 * Handles the init event received from the loader.
 *
 * The listener is removed after the init event is received, and all pending resolvers are resolved with the init result.
 */
function handleInitEvent(event: MessageEvent): void {
  if (!event.ports.length) return;
  const message = event.data as AppEvent;
  if (message.eventId !== AppEventId.INIT) return;

  const result: InitResult = {
    port: event.ports[0],
    compilationConfig: message.data,
    hostSetup: message.hostSetup ?? DEFAULT_HOST_SETUP_OPTIONS,
  };
  resolvedInitEvent = result;
  window.removeEventListener("message", handleInitEvent);
  pendingResolvers.forEach((resolve) => resolve(result));
  pendingResolvers = [];
}

/**
 * Waits for the init event to be received.
 *
 * @note This function is used before the app is bootstrapped, to ensure that the Event Bus is ready to receive messages.
 */
export function resolveInitEvent(): Promise<InitResult> {
  if (resolvedInitEvent) return Promise.resolve(resolvedInitEvent);
  window.addEventListener("message", handleInitEvent);
  return new Promise<InitResult>((resolve) => {
    pendingResolvers.push(resolve);
  });
}

/**
 * Returns the resolved init event if it has already been received, or null otherwise.
 */
export function getResolvedInitEvent(): InitResult | null {
  return resolvedInitEvent;
}
